/**
 * `clause-candidates` — FR-16, FR-17, CR-01, NFR-06, NFR-07 · CLM-36.
 *
 * The **Legal review queue** for `architecture.md` §6's clause library mining:
 * the operator CLI segments, normalises, embeds, clusters, ranks and describes,
 * and lands candidates here for a *person* to publish from. Three endpoints —
 * list, drill down, decide — and only the third writes anything.
 *
 * ## The pipeline proposes; a reviewer disposes
 *
 * FR-17: *"all authorised may read and reuse; only reviewers may edit master
 * text."* That sentence is why nothing in this contract lets the miner publish.
 * `accept` is the only decision that writes a `clause_library` row, it requires
 * `clause:edit_master_text` (Legal only), and the reviewer supplies both the
 * `master_text` and the `clause_code` in the request body. The model's proposal
 * is pre-filled in the response so the reviewer is editing rather than typing
 * from scratch — but a typo in the natural key every later revision hangs off is
 * then their deliberate act, not an inherited model artefact.
 *
 * ## Why the variant count is labelled "in scope"
 *
 * The cluster tables are corpus-level and have no party anchor, so the *cluster*
 * is readable by every authorised user (the `clause-embedding-queries.ts`
 * argument). Its **members** are paragraphs of specific contracts, and those are
 * scoped: `clause-candidates:cluster-variants` carries the `toScopeSqlPredicate`
 * fragment anchored on `root_contract_key`.
 *
 * The consequence is worth naming rather than hiding, because it is visible to
 * users: two Legal reviewers with different practice scopes can see **different
 * variant counts for the same cluster.** So the response says which count it is
 * giving — `variants_in_scope`, not `variants` — and carries the corpus-wide
 * `member_count` beside it, which discloses a total but no party. A field called
 * `variants` would read as a corpus fact and be a scoped one.
 *
 * ## No `apps/web` surface yet
 *
 * Phase 4.4 owns the React clause-review screen. This contract exists so that
 * screen has something to bind, and so the CLI's output has somewhere to land.
 */
import { z } from 'zod';
import { ClauseCodeSchema, ContractKeySchema, IsoTimestampUtcSchema, OpaqueIdSchema, Sha256Schema, TextSpanSchema, UpnSchema, } from '../primitives.js';
import { PageInfoSchema, PageRequestSchema } from '../pagination.js';
import { AuditReceiptSchema } from '../provenance.js';
/* -------------------------------------------------------------------------- */
/* Vocabulary                                                                  */
/* -------------------------------------------------------------------------- */
/**
 * Where a cluster's description stands. Mirrors
 * `ck_clause_cluster_description_state` (migration 0081) exactly.
 *
 * `withheld_restricted` and `refused_to_human` are deliberately distinct states,
 * not one "no description" state. The first is §4C working as designed — every
 * member is `processing_restricted`, so no model was called and none should be.
 * The second is rule 6 — a model refused, and the cluster is waiting for a
 * person, never for a second model. Collapsing them would make a compliance
 * decision and a refusal look like the same operational gap.
 */
export const CLUSTER_DESCRIPTION_STATES = [
    'pending',
    'described',
    'refused_to_human',
    /** §6: *"a clause appearing once can wait indefinitely"* — no model call spent. */
    'skipped_singleton',
    'withheld_restricted',
];
export const ClusterDescriptionStateSchema = z.enum(CLUSTER_DESCRIPTION_STATES);
/** Where a cluster stands with Legal. Mirrors `ck_clause_cluster_review_state`. */
export const CLUSTER_REVIEW_STATES = [
    'pending',
    'accepted',
    'rejected',
    'merged',
    'split',
];
export const ClusterReviewStateSchema = z.enum(CLUSTER_REVIEW_STATES);
/* -------------------------------------------------------------------------- */
/* The candidate                                                               */
/* -------------------------------------------------------------------------- */
/**
 * The AI-proposed library entry, with the provenance that makes it evidence.
 *
 * The four `description_*` fields travel **together or not at all**, which is
 * `ck_clause_cluster_description_provenance` (0081) restated on the wire and the
 * same rule `ck_clause_library_purpose_provenance` (0021) applies to a published
 * clause: an AI value without the model that produced it is not evidence (CR-01,
 * rule 7).
 *
 * `master_text` is absent, deliberately. The model does not propose clause
 * wording — FR-17 — and a field for it here would be the first place somebody
 * put one.
 */
export const ClauseCandidateDescriptionSchema = z.object({
    clause_code: ClauseCodeSchema.nullable(),
    title: z.string().min(1).max(200).nullable(),
    category: z.string().min(1).max(60).nullable(),
    /** WP-09 §4 representation 3. 1,000 characters — a summary, not a transcription. */
    purpose_text: z.string().min(1).max(1_000).nullable(),
    description_state: ClusterDescriptionStateSchema,
    /** §4C — every member is `processing_restricted`, so no model was called. */
    description_withheld: z.boolean(),
    description_provider_id: z.string().min(1).max(40).nullable(),
    description_model: z.string().min(1).max(120).nullable(),
    description_generated_utc: IsoTimestampUtcSchema.nullable(),
});
/**
 * §6's deviation intelligence — how tight the cluster is.
 *
 * `null` where the cluster has one distinct variant and therefore no pair to
 * measure. Not `1`: a mean over an empty set of pairs is undefined, and writing
 * `1` would claim a tightness measurement that was never made.
 */
export const ClauseCandidateDeviationSchema = z.object({
    mean_similarity: z.number().min(-1).max(1).nullable(),
    min_similarity: z.number().min(-1).max(1).nullable(),
    /** Distinct normalised texts. `1` means the cluster is one wording repeated. */
    variant_count: z.number().int().min(1),
});
/** One candidate clause awaiting review — a row of the queue. */
export const ClauseCandidateSchema = z.object({
    cluster_id: OpaqueIdSchema,
    /** Which run produced it. Its parameters are what make two runs comparable. */
    mining_run_id: OpaqueIdSchema,
    cluster_no: z.number().int().min(1),
    /**
     * Paragraphs in the cluster, **corpus-wide**. Discloses a count and no party,
     * which is why it is safe to give unscoped — see the module header.
     */
    member_count: z.number().int().min(1),
    distinct_contract_count: z.number().int().min(1),
    /** The heading trail the cluster was blocked on. The reviewer's first clue. */
    heading_path: z.array(z.string().min(1).max(200)).max(12),
    deviation: ClauseCandidateDeviationSchema,
    description: ClauseCandidateDescriptionSchema,
    review_state: ClusterReviewStateSchema,
    reviewed_by_upn: UpnSchema.nullable(),
    reviewed_utc: IsoTimestampUtcSchema.nullable(),
    /** Set only by `accept`. The `clause_library` row this cluster became. */
    published_clause_id: OpaqueIdSchema.nullable(),
});
/* -------------------------------------------------------------------------- */
/* GET /v1/clause-candidates                                                   */
/* -------------------------------------------------------------------------- */
export const ClauseCandidateListRequestSchema = z
    .object({
    /** Omit to see `pending` only — the queue, which is what the surface wants. */
    review_state: z.array(ClusterReviewStateSchema).min(1).optional(),
    /** Narrow to one run, so a re-cluster can be reviewed without the old one. */
    mining_run_id: OpaqueIdSchema.optional(),
    /**
     * Hide clusters no model described, whatever the reason.
     *
     * Off by default: a `withheld_restricted` cluster is a legitimate candidate a
     * reviewer can title by hand, and hiding it by default would quietly exclude
     * every restricted client's drafting from the library (§4C keeps those
     * contracts *"indexed and searchable"*, not invisible).
     */
    described_only: z.boolean().optional(),
    /** §6: most of the value is the top clusters. Ranked by frequency. */
    min_member_count: z.number().int().min(1).max(1_000).optional(),
    page: PageRequestSchema.optional(),
})
    .strict();
export const ClauseCandidateListResponseSchema = z.object({
    items: z.array(ClauseCandidateSchema),
    page: PageInfoSchema,
    /** Counters for the queue header. Corpus-level, like the clusters themselves. */
    totals: z.object({
        pending: z.number().int().min(0),
        described: z.number().int().min(0),
        /** §4C — queued with no model call. Shown as a policy state, never a failure. */
        withheld_restricted: z.number().int().min(0),
        /** Rule 6 — a model refused and a person has not yet looked. */
        refused_to_human: z.number().int().min(0),
        accepted: z.number().int().min(0),
    }),
    /** The newest run's parameters, so the reviewer knows what they are reviewing. */
    latest_run: z
        .object({
        mining_run_id: OpaqueIdSchema,
        started_utc: IsoTimestampUtcSchema,
        finished_utc: IsoTimestampUtcSchema.nullable(),
        normalise_rules_version: z.string().min(1).max(20),
        embedding_model: z.string().min(1).max(60).nullable(),
        join_threshold: z.number().min(0).max(1).nullable(),
        outcome: z.enum(['running', 'succeeded', 'failed']),
    })
        .nullable(),
});
/* -------------------------------------------------------------------------- */
/* GET /v1/clause-candidates/{cluster_id}                                      */
/* -------------------------------------------------------------------------- */
/**
 * One variant a reviewer can see, and where it came from.
 *
 * **Spans, not text** (rule 13). The reviewer's browser follows the span into the
 * contract record it is entitled to read; this contract does not carry a second
 * copy of the paragraph. `paragraph_sha256` is the normalised fingerprint, so two
 * variants that look alike on screen can be told apart definitively.
 */
export const ClauseCandidateVariantSchema = z.object({
    contract_clause_id: OpaqueIdSchema,
    contract_key: ContractKeySchema,
    paragraph_ref: z.string().min(1).max(60).nullable(),
    span: TextSpanSchema,
    paragraph_sha256: Sha256Schema,
    similarity_to_representative: z.number().min(-1).max(1).nullable(),
    is_representative: z.boolean(),
    /** §4C — shown as a deliberate policy state, so the reviewer knows why. */
    processing_restricted: z.boolean(),
});
export const ClauseCandidateDetailRequestSchema = z
    .object({ cluster_id: OpaqueIdSchema })
    .strict();
export const ClauseCandidateDetailResponseSchema = z.object({
    candidate: ClauseCandidateSchema,
    /**
     * The variants **within the caller's scope**, and the count of them.
     *
     * Named `variants_in_scope` rather than `variants` because it is a scoped
     * answer: `clause-candidates:cluster-variants` carries the predicate, so a
     * reviewer sees only members of contracts they may read. Two reviewers can
     * legitimately see different numbers here, and the field name is what stops
     * that reading as a data problem. `candidate.member_count` beside it is the
     * corpus-wide figure.
     */
    variants_in_scope: z.array(ClauseCandidateVariantSchema),
    variants_in_scope_count: z.number().int().min(0),
});
/* -------------------------------------------------------------------------- */
/* POST /v1/clause-candidates/{cluster_id}/decision                            */
/* -------------------------------------------------------------------------- */
/**
 * The four decisions, discriminated so each carries exactly what it needs.
 *
 * `accept` is not merely "the one that writes": it is the only member that
 * *can* carry `master_text`, so the FR-17 privilege and the field that exercises
 * it are the same shape. `reject`, `merge` and `split` have nowhere to put clause
 * wording, which is why the coarse-then-refined role gate in
 * `apps/api/src/routes/actions.ts` is a gate over a real distinction rather than
 * a convention.
 */
export const ClauseCandidateDecisionSchema = z.discriminatedUnion('decision', [
    z
        .object({
        /** Publish `clause_library` v1 from this cluster. `clause:edit_master_text`. */
        decision: z.literal('accept'),
        /**
         * **Typed by the reviewer**, not inherited from the proposal (ratified Q4).
         * `clause_code` is the natural key every later revision hangs off, so a typo
         * in it should be a person's deliberate act. The surface pre-fills the
         * model's suggestion; the reviewer confirms or changes it.
         */
        clause_code: ClauseCodeSchema,
        title: z.string().min(1).max(200),
        category: z.string().min(1).max(60).nullable(),
        /**
         * FR-17 — the authoritative wording, **written by the reviewer**. Never a
         * model output: the miner proposes a description, not a clause.
         *
         * Deliberately unbounded above 1 character, exactly as
         * `ck_clause_library_master_text` leaves it: a master clause can be long and
         * there is no honest ceiling to enforce.
         */
        master_text: z.string().min(1),
        /**
         * Carry the model's purpose description onto the published row, or replace
         * it. `null` publishes the clause with no purpose text, which is legitimate
         * — the `ix_clause_library_needs_purpose` partial index (0021) exists for
         * exactly that row.
         */
        purpose_text: z.string().min(1).max(1_000).nullable(),
        /**
         * Whether `purpose_text` above is the model's proposal unchanged.
         *
         * It decides whether the published row carries the `purpose_*` provenance
         * three: `true` copies the cluster's provider, model and timestamp across;
         * `false` means a person wrote it and the AI provenance would be a false
         * claim. `ck_clause_library_purpose_provenance` refuses anything between, and
         * this flag is how the API knows which side of it to write.
         */
        purpose_text_is_ai_proposal: z.boolean(),
    })
        .strict(),
    z
        .object({
        /** Not a library clause. `clause:review_match` — Admin may do this. */
        decision: z.literal('reject'),
        /** Required: a rejected candidate with no reason is a decision nobody can review. */
        reason: z.string().min(1).max(1_000),
    })
        .strict(),
    z
        .object({
        /** The same clause as another cluster. Neither row is published by this. */
        decision: z.literal('merge'),
        merge_into_cluster_id: OpaqueIdSchema,
        reason: z.string().min(1).max(1_000),
    })
        .strict(),
    z
        .object({
        /**
         * A family of variants, not one clause — the honest decision when
         * `min_similarity` sits barely above the join threshold (D-C1's stated gap).
         *
         * It writes no `clause_library` row and creates no new clusters: the cluster
         * is marked `split` and a re-cluster at a higher threshold is the fix. A
         * decision that silently re-partitioned the corpus from a review screen
         * would be the mining run, not a review.
         */
        decision: z.literal('split'),
        reason: z.string().min(1).max(1_000),
    })
        .strict(),
]);
export const ClauseCandidateDecisionRequestSchema = z
    .object({
    cluster_id: OpaqueIdSchema,
    /**
     * The `review_state` the reviewer's screen was showing.
     *
     * Sent so a stale screen is a `409 conflict` rather than a silent overwrite of
     * somebody else's decision. `clause-candidates:record-decision` also guards on
     * `review_state = 'pending'` server-side; this makes the reviewer's assumption
     * explicit, the same way `verify-field` sends `seen_extracted_value`.
     */
    seen_review_state: ClusterReviewStateSchema,
    decision: ClauseCandidateDecisionSchema,
})
    .strict();
export const ClauseCandidateDecisionResponseSchema = z.object({
    candidate: ClauseCandidateSchema,
    /**
     * The published clause, on `accept` only. `null` for the other three, which
     * never write to `clause_library`.
     *
     * ⚑ It has **no embeddings yet.** An accepted cluster is an `is_current`
     * `clause_library` row, which is exactly what `currentClausesQuery()` selects,
     * so `pnpm -C apps/api embed-clauses` fills the three WP-09 §4 representations
     * on its next run. Until it does, the clause is lexically searchable and
     * semantically absent — deliberate, because a second vector write path here
     * would be the "no second master" rule broken for convenience.
     */
    published_clause: z
        .object({
        clause_id: OpaqueIdSchema,
        clause_code: ClauseCodeSchema,
        version_no: z.number().int().min(1),
        is_current: z.boolean(),
        created_by_upn: UpnSchema,
    })
        .nullable(),
    /** NFR-07 — the row this decision wrote, in the same transaction as the decision. */
    audit: AuditReceiptSchema,
});
//# sourceMappingURL=clause-candidates.js.map